/**
 * TA-199. One environment preflight, for every long-lived process.
 *
 * THE DEFECT THIS CLOSES. Both live processes decided their configuration one
 * variable at a time, in the order the code happened to reach them. The worker
 * refuses on `DATABASE_URL` at `main.ts` line 140 and only then resolves
 * `ACKRA_ARTIFACTS` at line 169, so an operator with both wrong restarts twice
 * to learn two facts. Everything else was worse than that: the API's `PORT` is
 * `Number(process.env["PORT"] ?? 4000)` with no validation, so `PORT=8080x`
 * becomes `NaN` and `server.listen(NaN)` binds a RANDOM free port while the
 * banner prints `NaN`, and the operator's monitoring watches 8080 forever.
 * `META_APP_SECRET` and `ACKRA_PLATFORM_REF_SECRET` default to `""`, and the
 * signature check fails closed on an empty secret (`metaWebhook.ts` line 33),
 * so an API deployed without them answers **401 to every genuine Meta
 * delivery** while `GET /health` reports `ok: true`. That is the silent
 * degradation CLAUDE.md section 0 names, with the loudness pointed at the
 * customer instead of the operator.
 *
 * THE RULE THIS FILE IMPLEMENTS, in one sentence: every problem an operator
 * can fix by editing the environment is found in ONE pass, named together,
 * before a socket is opened or a queue is polled.
 *
 * THREE SEVERITIES, because "required" is not one question:
 *
 *  - `refuse`   the process cannot do its job at all. It does not start.
 *  - `degrade`  the process runs and something real is lost. Loud, then start.
 *               This is section 0's "loud to the operator, graceful to the
 *               customer" written as a severity.
 *  - `allow`    there is a documented default and nothing is lost.
 *
 * A SET value that fails `validate` ALWAYS refuses, whatever the severity of
 * its absence. That asymmetry is deliberate and it is `resolveArtifactSource`'s
 * own argument generalised: an unset variable is an intent that is merely
 * unmet, and a variable holding `pipline` is an intent nothing can read, so
 * nothing else in that environment can be trusted either.
 *
 * SECRECY (CLAUDE.md section 12: credential names only, values in env vars).
 * A spec marked `secret` never has its value printed, on any path, including
 * the message that says the value is wrong. Naming a bad `ACKRA_ARTIFACTS`
 * value is the fix; naming a bad `DATABASE_URL` value leaks a password into a
 * container log. A spec that is not a credential but can be made to hold one
 * (`PUBLIC_APP_URL`, TA-276) declares `redact`, and only its redacted copy is
 * ever placed in a problem.
 *
 * This module knows nothing about any particular process. Each app declares
 * its own specs (`apps/worker/src/envSpec.ts`, `apps/api/src/envSpec.ts`),
 * because what a variable's absence COSTS is a fact about that process, and a
 * shared list would have to lie about one of them.
 */

/** What the absence of a variable means for the process that declared it. */
export type MissingSeverity = "refuse" | "degrade" | "allow";

export interface EnvVarSpec {
  readonly name: string;
  /**
   * True when the value is a credential. Its value is never rendered, not even
   * when it is the value that is wrong.
   */
  readonly secret?: boolean;
  /**
   * TA-276. For a value that is not a credential but can be made to carry one:
   * `PUBLIC_APP_URL=https://name:password@host` is the case that added it.
   * Given the value as set, returns the copy that may be printed. Applied
   * where `checkEnv` places a non-secret value in a problem, which is the only
   * copy either renderer prints, so nothing downstream sees the raw value.
   * `validate` still reads the value as set. It never applies to a `secret`
   * spec, whose value is withheld whole. A `note` that quotes a value is its
   * own author's to redact.
   */
  readonly redact?: (raw: string) => string;
  /** What an operator needs this for, in one clause. */
  readonly purpose: string;
  /** What happens when it is unset. */
  readonly whenMissing: MissingSeverity;
  /** The consequence of the absence, in an operator's words, not a developer's. */
  readonly missingMeans: string;
  /**
   * Checks a value that IS set. Returns a problem sentence, or null.
   *
   * Must never include the value in its message: the renderer adds the value
   * itself, and only when the spec is not `secret`.
   */
  readonly validate?: (value: string) => string | null;
  /**
   * Promotes this variable to a refusal given the rest of the environment.
   * Returns the reason it is required here, or null.
   *
   * Exists because "required" is often conditional: a deployment that serves
   * Meta traffic needs a `META_APP_SECRET` and one that does not is a
   * supported shape (`apps/api/src/artifacts.ts` argues exactly this about
   * `DATABASE_URL`).
   */
  readonly requiredWhen?: (env: EnvRecord) => string | null;
  /**
   * TA-417. Promotes an `allow` variable to a degradation given the rest of
   * the environment. Returns the reason its absence costs something here, or
   * null. Only ever promotes: it never softens a `refuse`, and `requiredWhen`
   * outranks it.
   *
   * Exists for the mirror of `requiredWhen`'s case: a variable whose absence
   * is free in one shape of a process and a real loss in another. The API's
   * `ANTHROPIC_API_KEY` added it. Its only model route is `POST /send`, off
   * unless ACKRA_ENABLE_DEMO_SEND=1, and a flat `degrade` put a degradation
   * that cost nothing on every deployment without the route, which made
   * `GET /ready?strict=1` unreachable on the AWS api task by construction.
   */
  readonly degradeWhen?: (env: EnvRecord) => string | null;
  /**
   * A standing fact worth printing even when nothing is wrong, given the
   * environment. Returns the line, or null.
   */
  readonly note?: (env: EnvRecord) => string | null;
}

export type EnvRecord = Readonly<Record<string, string | undefined>>;

export type ProblemKind = "missing" | "invalid";

export interface EnvProblem {
  readonly name: string;
  readonly problem: ProblemKind;
  readonly purpose: string;
  /** Why this is a problem HERE: the consequence, or the requiredWhen or degradeWhen reason. */
  readonly because: string;
  /**
   * The offending value, present only when the spec is not `secret`, and
   * already passed through the spec's `redact` when it has one (TA-276).
   */
  readonly value?: string;
}

export interface PreflightReport {
  /** The process this describes, as it appears in the banner. */
  readonly process: string;
  /** Everything that stops the process starting. All of it, never the first. */
  readonly refusals: readonly EnvProblem[];
  /** Everything that is lost but does not stop the process. */
  readonly degradations: readonly EnvProblem[];
  /** Standing facts worth saying out loud. */
  readonly notes: readonly string[];
  readonly ok: boolean;
}

/**
 * Checks a Postgres connection URL without ever reporting its contents.
 *
 * Lives here, in the one package both apps and `packages/db` already depend
 * on, because `apps/worker` and `apps/api` both need it and an app importing
 * from another app is the architectural regression `artifactSource.ts` refused
 * to make for exactly this reason.
 *
 * Only the scheme, the host and parseability, which is all that can be checked
 * without opening a socket. `new Pool({ connectionString })` validates nothing
 * at construction, so a URL with the wrong scheme surfaces as a failed query
 * on the first tick: one `tick failed` line every two seconds, forever, with
 * nothing anywhere saying the string itself was never going to work.
 */
export function validatePostgresUrl(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return "is not a URL at all. Expected postgres://user:password@host:port/database";
  }
  if (url.protocol !== "postgres:" && url.protocol !== "postgresql:") {
    return (
      `has scheme "${url.protocol.replace(":", "")}", and node-postgres only speaks ` +
      `postgres:// or postgresql://. The value itself is withheld because it carries ` +
      `a password`
    );
  }
  if (url.hostname === "") return "names no host";
  return null;
}

/**
 * TA-341. The one trimmed env read: the value with its ends trimmed, or
 * undefined when the variable is unset, empty, or whitespace only. All three
 * are "the operator set nothing", the rule `checkEnv` has always applied and
 * `.env.example`'s empty lines exist to test (TA-257).
 *
 * Thirteen sites spelled this by hand as `(env[NAME] ?? "").trim()` or
 * `env[NAME]?.trim()`, and fifteen by the time it landed. Never returns "", so
 * `envValue(env, NAME) ?? ""` is exactly the old `(env[NAME] ?? "").trim()`.
 * Whitespace is `String.prototype.trim`'s, which is what every one of those
 * sites used. `metaImport.ts` trims `ACKRA_META_OWNER` to ASCII only on
 * purpose (TA-276) and does not come through here.
 * `envReads.chokepoint.test.ts` keeps this the only one, bar the single
 * allowance it names with its reason (`clientId.ts`).
 */
export function envValue(env: EnvRecord, name: string): string | undefined {
  const trimmed = env[name]?.trim();
  return trimmed === undefined || trimmed === "" ? undefined : trimmed;
}

/** Unset, empty, or whitespace only: whatever `envValue` reads as nothing (TA-341). */
function isUnset(env: EnvRecord, name: string): boolean {
  return envValue(env, name) === undefined;
}

/**
 * Reads the whole environment against the whole spec list and reports
 * everything wrong with it at once.
 *
 * Order is the spec list's order, so the output reads in the order a runbook
 * explains the variables rather than in the order a hash table enumerates them.
 */
export function checkEnv(
  processName: string,
  specs: readonly EnvVarSpec[],
  env: EnvRecord,
): PreflightReport {
  const seen = new Set<string>();
  for (const spec of specs) {
    if (seen.has(spec.name)) {
      // A duplicate spec means two places disagree about what one variable is
      // for, and whichever one loses does so silently. Section 0.
      throw new Error(
        `env preflight for ${processName} declares ${spec.name} twice. One ` +
          `variable, one spec: two would mean two answers to "is this required" ` +
          `and only one of them would ever be printed.`,
      );
    }
    seen.add(spec.name);
  }

  const refusals: EnvProblem[] = [];
  const degradations: EnvProblem[] = [];
  const notes: string[] = [];

  for (const spec of specs) {
    // Read as set: `validate` and `redact` are handed the value untrimmed, as
    // they always have been. Only the unset test goes through `envValue`.
    const raw = env[spec.name];

    if (isUnset(env, spec.name)) {
      const promoted = spec.requiredWhen?.(env) ?? null;
      // TA-417: asked only of an `allow` spec that `requiredWhen` left alone,
      // so it can raise a severity and never lower one.
      const degradedBy =
        promoted === null && spec.whenMissing === "allow" ? (spec.degradeWhen?.(env) ?? null) : null;
      const reason = promoted ?? degradedBy;
      const severity: MissingSeverity =
        promoted !== null ? "refuse" : degradedBy !== null ? "degrade" : spec.whenMissing;
      const problem: EnvProblem = {
        name: spec.name,
        problem: "missing",
        purpose: spec.purpose,
        because: reason !== null ? `${reason}. Without it, ${spec.missingMeans}` : spec.missingMeans,
      };
      if (severity === "refuse") refusals.push(problem);
      else if (severity === "degrade") degradations.push(problem);
    } else {
      const complaint = spec.validate?.(raw!) ?? null;
      if (complaint !== null) {
        refusals.push({
          name: spec.name,
          problem: "invalid",
          purpose: spec.purpose,
          because: complaint,
          // The one place secrecy has teeth: a wrong DATABASE_URL is still a
          // DATABASE_URL, and printing it puts a password in a container log.
          // TA-276: and the one place redaction does, for the same reason, so
          // neither renderer can print a copy that skipped it.
          ...(spec.secret === true ? {} : { value: spec.redact ? spec.redact(raw!) : raw! }),
        });
      }
    }

    const note = spec.note?.(env) ?? null;
    if (note !== null) notes.push(note);
  }

  return {
    process: processName,
    refusals,
    degradations,
    notes,
    ok: refusals.length === 0,
  };
}

const INDENT = "    ";

/**
 * `stops` is not cosmetic. A degradation is BY DEFINITION the case where the
 * process keeps going, so printing "why it stops here" under one contradicts
 * the paragraph directly above it that just said it starts anyway. An operator
 * who catches a banner telling them two different things in six lines stops
 * reading banners, and this one is the only warning some of these variables
 * ever get.
 */
function problemLines(p: EnvProblem, stops: boolean): string[] {
  const head =
    p.problem === "missing"
      ? `${INDENT}${p.name}  NOT SET`
      : p.value === undefined
        ? `${INDENT}${p.name}  INVALID (value withheld: this is a credential)`
        : `${INDENT}${p.name}  INVALID: "${p.value}"`;
  return [
    head,
    `${INDENT}    what it is:  ${p.purpose}`,
    `${INDENT}    ${stops ? "why it stops here" : "what its absence costs"}:  ${p.because}`,
  ];
}

/**
 * One problem as one line, for somewhere an indented block is useless.
 *
 * `GET /ready` counts this deployment's degradations (`degraded`, and the
 * 503 on `?strict=1`), and they have to be the SAME degradations the banner
 * printed or the probe and the log describe two systems. Two renderers over
 * one list; never two lists. Since decision list item 36 the strings stay off
 * `/ready`'s public body: this line form is what the route keeps beside it.
 *
 * The secrecy rule holds here exactly as it does above: a `secret` spec never
 * carries a `value`, so there is nothing for this to print even by accident.
 */
export function describeProblem(p: EnvProblem): string {
  const head =
    p.problem === "missing"
      ? `${p.name} is not set`
      : p.value === undefined
        ? `${p.name} is invalid (value withheld: this is a credential)`
        : `${p.name} is invalid ("${p.value}")`;
  return `${head}: ${p.because}`;
}

/**
 * The operator-facing text. Empty when there is nothing to say, so a caller
 * can write it unconditionally without printing a blank block on a clean boot.
 *
 * Shaped like the refusals already in this repo (`main.ts`,
 * `artifactSource.ts`): indented, no stack trace, and every entry carries the
 * repair rather than the diagnosis alone.
 */
export function renderPreflight(report: PreflightReport): string[] {
  if (report.refusals.length === 0 && report.degradations.length === 0 && report.notes.length === 0) {
    return [];
  }

  const lines: string[] = [""];

  if (report.refusals.length > 0) {
    const n = report.refusals.length;
    lines.push(
      `  ${report.process} PREFLIGHT FAILED`,
      "",
      `  ${n} environment problem${n === 1 ? "" : "s"}, and this is all of them, not the`,
      `  first one the code happened to reach:`,
      "",
    );
    for (const p of report.refusals) lines.push(...problemLines(p, true), "");
    lines.push(
      `  ${report.process} refuses to start. Nothing was opened and no queue is`,
      `  being polled. Fix every line above in one edit: a process that names one`,
      `  problem per restart costs an operator a restart per problem.`,
      "",
    );
  }

  if (report.degradations.length > 0) {
    const n = report.degradations.length;
    lines.push(
      `  ${report.process} DEGRADED: ${n} thing${n === 1 ? "" : "s"} this deployment cannot do.`,
      `  It starts anyway. CLAUDE.md section 0: loud to the operator, graceful to`,
      `  the customer.`,
      "",
    );
    for (const p of report.degradations) lines.push(...problemLines(p, false), "");
  }

  if (report.notes.length > 0) {
    for (const note of report.notes) lines.push(`  NOTE  ${note}`);
    lines.push("");
  }

  return lines;
}
